import React, { useCallback, useMemo } from 'react';
import TrueImpactAddButton from '../TrueImpactAddButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import { faClose } from '@fortawesome/pro-light-svg-icons';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import styled from 'styled-components';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import {
  INDICATOR_STAGES_KEY,
  IndicatorSectionKey,
  IndicatorWithUpdate,
} from '../utils';
import { capitalizeFirstLetter } from 'shared/utils/stringUtils';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import RestoreIndicator from './RestoreIndicator';
import {
  DeletedIndicator,
  DeletedIndicatorEntry,
} from 'shared/providers/IndicatorProvider/utils';

interface StagesModalProps {
  open: boolean;
  disabled?: boolean;
  maxWidth?: number;
  header: string;
  buttonText?: JSX.Element | string;
  className?: string;
  hideAddButton?: boolean;
  marginTop?: string;
  sectionKey: IndicatorSectionKey;
  indicators: IndicatorWithUpdate[];
  selectedIndicators: IndicatorWithUpdate[];
  handleAdd: (indicator: IndicatorWithUpdate, index: number) => unknown;
  handleOpen: () => unknown;
  handleClose: () => unknown;
}

const getLabel = (title?: string, description?: string) => {
  return (
    <>
      {title && <TrueImpactText variant="body-bold">{title}</TrueImpactText>}
      {description && <TrueImpactText>{description}</TrueImpactText>}
    </>
  );
};

const StyledDiv = styled.div`
  .heading-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .close-button {
      margin-right: -${SPACERS.tiny};
    }
    h2 {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    background-color: ${COLORS.grayLight};
  }
  .indictor-option {
    margin-top: ${SPACERS.tiny};
    margin-bottom: ${SPACERS.tiny};
  }
`;

const StagesModal: React.FC<StagesModalProps> = ({
  buttonText = 'Add Indicator',
  maxWidth,
  disabled,
  open,
  header,
  marginTop,
  indicators,
  selectedIndicators,
  className,
  hideAddButton,
  sectionKey,
  handleAdd,
  handleClose,
  handleOpen,
}) => {
  const {
    impactDetails,
    deletedIndicators,
    handleRestoreIndicator,
  } = useIndicator();

  const selectedIds = useMemo(
    () => new Set(selectedIndicators.map(s => s.id)),
    [selectedIndicators],
  );

  const deletedForSection = useMemo(() => {
    return Object.values(deletedIndicators).filter((d): d is NonNullable<
      typeof d
    > =>
      d
        ? INDICATOR_STAGES_KEY[d.indicatorSummary.stageId] === sectionKey &&
          !selectedIds.has(d.indicatorSummary.id)
        : false,
    );
  }, [deletedIndicators, sectionKey, selectedIds]);

  const filteredIndicators = useMemo(() => {
    const hasHumanSelected =
      impactDetails.reach.some(r => r.isHuman) ||
      selectedIndicators.some(s => s.isHuman);
    const base =
      hasHumanSelected && sectionKey === 'reach'
        ? indicators.filter(i => !i.isHuman)
        : indicators;
    return base.filter(i => !selectedIds.has(i.id));
  }, [
    impactDetails.reach,
    indicators,
    sectionKey,
    selectedIds,
    selectedIndicators,
  ]);

  const handleRestore = useCallback(
    (d: DeletedIndicatorEntry) => {
      handleRestoreIndicator(
        sectionKey,
        d.indicatorSummary.id,
        d.indicatorSummary,
        d.indicatorDetails,
      );
    },
    [sectionKey, handleRestoreIndicator],
  );
  return (
    <>
      {deletedForSection.map((d, i) => (
        <RestoreIndicator
          index={i}
          key={d.indicatorSummary.id}
          indicator={d.indicatorSummary}
          sectionKey={sectionKey}
          handleRestoreIndicator={() => handleRestore(d)}
        />
      ))}
      {!hideAddButton && (
        <TrueImpactAddButton
          text={buttonText}
          disabled={disabled}
          maxWidth={maxWidth}
          marginTop={marginTop}
          onClick={handleOpen}
          className={className}
        />
      )}
      <Dialog
        fullScreen={false}
        open={open}
        onClose={handleClose}
        // disableBackdropClick={true}
        aria-labelledby={header}
        fullWidth={true}
        scroll="paper"
      >
        <StyledDiv>
          <DialogTitle id={header} className="heading-title">
            <TrueImpactText variant="body-bold">
              {header.toUpperCase()}
            </TrueImpactText>
            <TrueImpactIconButton
              color="blackPure"
              icon={faClose}
              onClick={handleClose}
              className="close-button"
            />
          </DialogTitle>
          <DialogContent dividers={true}>
            <DialogContentText>
              <SelectorOptions
                variant="secondary-no-icon"
                options={filteredIndicators}
                getLabel={val =>
                  getLabel(capitalizeFirstLetter(val.name), val.definition)
                }
                getSelected={val => false}
                onClick={handleAdd}
              />
            </DialogContentText>
          </DialogContent>
        </StyledDiv>
      </Dialog>
    </>
  );
};

export default StagesModal;
